import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {Container} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import {useHistory} from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import Skeleton from "react-loading-skeleton";
import useAxios from "../../utils/useAxios";
import MenuList from "../../components/MenuList";
import {CustomModal} from "../../components/CustomModel/CustomModal";
import useModal from "../../hooks/useModal";
import PlayerBaseForm from "./PlayerBaseForm";
import SharePlayerBaseModal from "../../components/SharePlayerBaseModal";
import AuthContext from "../../contexts/AuthContext";
import {getInitials} from "../../utils/getInitials";
import { prefetchUrl } from "../../utils/tabPrefetch";
import { CompactTableRow } from "../../components/CompactTableRow";
import useCompactToolbar from "../../hooks/useCompactToolbar";

const PlayerBaseList = () => {
    let {user} = useContext(AuthContext);
    const {t} = useTranslation();
    const api = useAxios();
    const history = useHistory();
    const {modalIsOpen, openModal, setIsOpen} = useModal(false);
    // Compact Table is a user-facing-only density preference (see
    // TournamentsList.js/ClubsList.js) - this list is admin-only with no
    // public counterpart to ever show it in, so it's always off here
    // regardless of the stored setting.
    const compactTable = false;
    const searchInputRef = useRef(null);

    const [bases, setBases] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingBase, setEditingBase] = useState(null);
    const [search, setSearch] = useState("");

    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [shareEndpoint, setShareEndpoint] = useState("");

    const openShareModal = (endpoint) => {
        setShareEndpoint(endpoint);
        setIsShareModalOpen(true);
    };

    useEffect(() => {
        document.title = t("Player bases");
        fetchBases();
    }, []);

    const fetchBases = async () => {
        setLoading(true);
        try {
            const res = await prefetchUrl(api, "/api/v1.0/players/base/");
            setBases(res.data.results || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingBase(null);
        openModal();
    };

    const handleOpenBase = (base) => {
        history.push({
            pathname: `/admin/bases/${base.id}/players`,
            state: {
                baseName: base.name,
                canWrite: base.write
            }
        });
    };

    const visibleBases = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return bases;
        return bases.filter(base => (base.name || "").toLowerCase().includes(query));
    }, [bases, search]);

    const { toolbarSentinelRef, progress: toolbarProgress, activated: toolbarActivated, collapse: collapseToolbar } = useCompactToolbar({
        onExpand: () => {
            window.scrollTo(0, 0);
            searchInputRef.current?.focus();
        },
        onCreate: handleCreate
    });

    // See TournamentsList.js/ClubsList.js/RatingList.js's own copy of this
    // same formula for the full story: paddingBottom mirrors paddingTop's
    // 10*(1-progress) once activated (10px expanded, shrinking to 0 once
    // scrolled away, flush against the header again) - but before the row's
    // ever been opened this mount, progress is permanently pinned at 1
    // regardless of scroll position, which would collapse this formula to a
    // flat 0 the entire time the page is scrolled. The flat 10px override
    // is only for that one specific "never opened yet" state, so it doesn't
    // pin a permanent gap-colored strip under the header for the rest of
    // the scroll.
    // With an active search the row stays fully expanded regardless of
    // scroll, instead of auto-collapsing away - same fix as
    // TournamentsList.js's own hasActiveSearch/effectiveToolbarProgress.
    const hasActiveSearch = search.trim().length > 0;
    const effectiveToolbarProgress = hasActiveSearch ? 0 : toolbarProgress;

    const toolbarStyle = {
        paddingTop: `${5 * (1 - effectiveToolbarProgress)}px`,
        paddingBottom: toolbarActivated ? `${10 * (1 - effectiveToolbarProgress)}px` : "10px"
    };
    const searchRowStyle = {
        maxHeight: `${60 * (1 - effectiveToolbarProgress)}px`,
        opacity: 1 - effectiveToolbarProgress,
        pointerEvents: effectiveToolbarProgress >= 0.99 ? "none" : "auto"
    };

    return (
        <>
            <Container className="page_container">
                <div ref={toolbarSentinelRef} style={{ height: "1px" }} />
                <div className="sticky-toolbar" style={toolbarStyle}>
                    <nav className="tabs-container toolbar-search-row" style={searchRowStyle}>
                        <ul className="tabs">
                            <li style={{ position: "relative", width: "100%" }}>
                                <FeatherIcon icon="search" size={16} className="search-icon" />
                                <input
                                    ref={searchInputRef}
                                    className="search"
                                    type="text"
                                    placeholder={t("Player bases search: name...")}
                                    onKeyUp={e => setSearch(e.target.value)}
                                    onBlur={e => { if (!e.target.value) collapseToolbar(); }}
                                    style={{ paddingLeft: "30px" }}
                                />
                            </li>
                        </ul>
                    </nav>
                </div>

                {compactTable ? (
                    <div className="trows">
                        {loading
                            ? Array.from({length: 5}).map((_, idx) => (
                                <div className="trow trow-bases" key={`skel-base-${idx}`} style={{ pointerEvents: "none" }}>
                                    <Skeleton circle width={20} height={20} />
                                    <Skeleton width="60%" height={12} />
                                    <Skeleton width="80%" height={11} />
                                    <Skeleton width="80%" height={11} />
                                    <span />
                                </div>
                            ))
                            : visibleBases.map((base, idx) => (
                                <CompactTableRow
                                    key={base.id}
                                    variant="bases"
                                    zebra={idx % 2 === 1}
                                    title={base.name || t("Unnamed base")}
                                    onClick={() => handleOpenBase(base)}
                                    stats={[
                                        { icon: "users", value: base.counter_players > 0 ? base.counter_players : "—" },
                                        { icon: "share-2", value: (base.creator === user?.user_id && base.access_users?.length > 0) ? base.access_users.length : "—" }
                                    ]}
                                    actions={base.creator === user?.user_id && (
                                        <MenuList
                                            deleteEndpoint={`/api/v1.0/players/base/${base.id}`}
                                            onDeleted={() =>
                                                setBases(prev => prev.filter(b => b.id !== base.id))
                                            }
                                            successMessage="Base deleted successfully"
                                            errorMessage="Error deleting base"
                                            is_edit
                                            onEdit={() => {
                                                setEditingBase(base);
                                                openModal();
                                            }}
                                            editText={t("Edit base")}
                                            is_share
                                            onShare={() =>
                                                openShareModal(
                                                    `/api/v1.0/players/base/${base.id}/share/`
                                                )
                                            }
                                            asModal
                                        />
                                    )}
                                />
                            ))}
                    </div>
                ) : (
                <div className="entity-cards">
                    {loading
                        ? Array.from({length: 5}).map((_, idx) => (
                            <div className="entity-card" key={`skel-base-${idx}`} style={{ pointerEvents: "none" }}>
                                <div className="entity-card-top">
                                    <div className="entity-card-thumb" style={{ background: "transparent" }}>
                                        <Skeleton circle width={52} height={52} />
                                    </div>
                                    <div className="entity-card-titles">
                                        <div className="entity-card-title"><Skeleton width="65%" height={15} /></div>
                                        <div className="entity-card-subtitle"><Skeleton width="45%" height={12} style={{ marginTop: 4 }} /></div>
                                    </div>
                                </div>
                            </div>
                        ))
                        : visibleBases.map(base => (
                            <div
                                className="entity-card"
                                key={base.id}
                                onClick={() => handleOpenBase(base)}
                            >
                                {base.creator === user?.user_id && (
                                    <div className="entity-card-actions" onClick={e => e.stopPropagation()}>
                                        <MenuList
                                            deleteEndpoint={`/api/v1.0/players/base/${base.id}`}
                                            onDeleted={() =>
                                                setBases(prev => prev.filter(b => b.id !== base.id))
                                            }
                                            successMessage="Base deleted successfully"
                                            errorMessage="Error deleting base"
                                            is_edit
                                            onEdit={() => {
                                                setEditingBase(base);
                                                openModal();
                                            }}
                                            editText={t("Edit base")}
                                            is_share
                                            onShare={() =>
                                                openShareModal(
                                                    `/api/v1.0/players/base/${base.id}/share/`
                                                )
                                            }
                                            asModal
                                        />
                                    </div>
                                )}
                                <div className="entity-card-top">
                                    <div className="entity-card-thumb">
                                        <div className="no-thumbnail initials">{getInitials(base.name)}</div>
                                    </div>
                                    <div className="entity-card-titles">
                                        <div className="entity-card-title">{base.name || t("Unnamed base")}</div>
                                        <div className="entity-card-subtitle">
                                            {base.counter_players > 0
                                                ? `${base.counter_players} ${t("Players")}`
                                                : t("No players")}
                                        </div>
                                    </div>
                                    {base.creator === user?.user_id && base.access_users?.length > 0 && (
                                        <div className="entity-card-users-count">
                                            {base.access_users.length} {t("Users")}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                </div>
                )}

                <CustomModal isOpen={modalIsOpen} onClose={() => setIsOpen(false)}>
                    <PlayerBaseForm
                        baseId={editingBase?.id || null}
                        onClose={() => setIsOpen(false)}
                        onSuccess={item => {
                            setBases(prev => {
                                if (!item?.id) return prev;
                                const index = prev.findIndex(b => b.id === item.id);
                                if (index !== -1) {
                                    const updated = [...prev];
                                    updated[index] = item;
                                    return updated;
                                }
                                return [item, ...prev];
                            });
                            setIsOpen(false);
                        }}
                    />
                </CustomModal>
            </Container>

            <SharePlayerBaseModal
                isOpen={isShareModalOpen}
                onClose={() => {
                    setIsShareModalOpen(false);
                    fetchBases();
                }}
                endpoint={shareEndpoint}
            />
        </>
    );
};

export default PlayerBaseList;
